/**
 * Switch Component
 * 
 * A toggle switch component for boolean inputs with various states and styles.
 * Implements accessibility features and integrates with the design system.
 */
import { forwardRef, InputHTMLAttributes } from 'react'
import { cn } from '../../utils/tailwind-utils'

interface SwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /**
   * Label for the switch
   */
  label?: string
  
  /**
   * Description text to display below the switch
   */
  description?: string
  
  /**
   * Error message to display
   */
  error?: string
  
  /**
   * Additional CSS classes for the wrapper
   */
  wrapperClassName?: string
  
  /**
   * Size variant of the switch
   * @default "md"
   */
  size?: 'sm' | 'md' | 'lg'
  
  /**
   * Whether the label should appear on the left
   * @default false
   */
  labelLeft?: boolean
}

/**
 * Switch component for boolean input with toggle functionality
 * 
 * @example
 * // Basic usage
 * <Switch checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />
 * 
 * @example
 * // With label and description
 * <Switch 
 *   label="Enable notifications"
 *   description="You'll receive updates about new events"
 *   checked={notifications}
 *   onChange={(e) => setNotifications(e.target.checked)}
 * />
 * 
 * @example
 * // Small size with label on left
 * <Switch 
 *   label="Dark mode"
 *   size="sm"
 *   labelLeft
 *   checked={isDarkMode}
 *   onChange={toggleDarkMode}
 * />
 */
const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      className,
      wrapperClassName,
      label,
      description,
      error,
      size = 'md',
      disabled,
      labelLeft = false,
      ...props
    },
    ref
  ) => {
    // Size classes mapping
    const sizeClasses = {
      sm: {
        track: 'w-8 h-4',
        thumb: 'w-3 h-3',
        thumbTranslate: 'translate-x-4',
      },
      md: {
        track: 'w-11 h-6',
        thumb: 'w-5 h-5',
        thumbTranslate: 'translate-x-5',
      },
      lg: {
        track: 'w-14 h-7',
        thumb: 'w-6 h-6',
        thumbTranslate: 'translate-x-7',
      },
    }
    
    // Label size classes mapping
    const labelSizeClasses = {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
    }
    
    // Determine if there's an error
    const hasError = !!error
    
    return (
      <div 
        className={cn(
          'flex items-center',
          labelLeft ? 'flex-row' : 'flex-row-reverse justify-end',
          wrapperClassName
        )}
      >
        {/* Switch control */}
        <div className="relative inline-flex items-center">
          <input
            type="checkbox"
            className="sr-only"
            disabled={disabled}
            ref={ref}
            {...props}
          />
          <div
            className={cn(
              'relative inline-flex flex-shrink-0 cursor-pointer rounded-full transition-colors ease-in-out duration-200',
              sizeClasses[size].track,
              props.checked ? 'bg-primary' : 'bg-muted',
              hasError && 'ring-2 ring-destructive',
              disabled && 'opacity-50 cursor-not-allowed',
              className
            )}
            onClick={() => {
              if (!disabled && props.onChange) {
                const newChecked = !props.checked
                props.onChange({
                  target: { checked: newChecked },
                } as React.ChangeEvent<HTMLInputElement>)
              }
            }}
            role="switch"
            aria-checked={props.checked}
            tabIndex={disabled ? -1 : 0}
            onKeyDown={(e) => {
              if (!disabled && (e.key === 'Enter' || e.key === ' ') && props.onChange) {
                e.preventDefault()
                const newChecked = !props.checked
                props.onChange({
                  target: { checked: newChecked },
                } as React.ChangeEvent<HTMLInputElement>)
              }
            }}
          >
            <span
              className={cn(
                'pointer-events-none inline-block rounded-full bg-white shadow-lg transform ring-0 transition ease-in-out duration-200',
                sizeClasses[size].thumb,
                props.checked ? sizeClasses[size].thumbTranslate : 'translate-x-0'
              )}
            />
          </div>
        </div>
        
        {/* Label and description */}
        {(label || description || error) && (
          <div className={cn(labelLeft ? 'mr-3' : 'ml-3')}>
            {label && (
              <label 
                htmlFor={props.id} 
                className={cn(
                  'font-medium',
                  labelSizeClasses[size],
                  hasError ? 'text-destructive' : 'text-foreground',
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                {label}
              </label>
            )}
            
            {description && (
              <p className="text-sm text-muted-foreground mt-1">
                {description}
              </p>
            )}
            
            {error && (
              <p className="text-sm text-destructive mt-1">
                {error}
              </p>
            )}
          </div>
        )}
      </div>
    )
  }
)

// Display name for debugging
Switch.displayName = 'Switch'

export default Switch
